// Booking fee & pricing policy — mirrors backend/server.py logic
// NOTE: No seasonal/off-peak discount. Only duration discounts + optional promo rates apply.

// ===== El Nido pickup/drop-off surcharge (per leg) =====
export const EL_NIDO_FEES = { hatchback: 6000, sedan: 6500, mpv: 7500, suv: 8000, pickup: 8000, van: 8500 };
// Motorbike El Nido fees by model keyword
export const MOTORBIKE_EL_NIDO_FEES = [["beat", 4000], ["click", 4500], ["aerox", 5000], ["adv", 5000], ["pcx", 5000]];

export const elNidoFeeFor = (vehicle) => {
  if (!vehicle) return 0;
  if (vehicle.type === "motorbike") {
    const label = `${vehicle.name || ""} ${vehicle.model || ""}`.toLowerCase();
    for (const [kw, fee] of MOTORBIKE_EL_NIDO_FEES) {
      if (label.includes(kw)) return fee;
    }
    return 5000;
  }
  return EL_NIDO_FEES[(vehicle.category || "").toLowerCase()] ?? 8000;
};

// ===== Optional add-on services =====
export const ADDONS = [
  { id: "car_seat", label: "Car seat", fee: 400, vehicleType: "car" },
  { id: "top_box", label: "Motorbike top box", fee: 300, vehicleType: "motorbike" },
];

export const addonsFor = (vehicle) => ADDONS.filter((a) => a.vehicleType === vehicle?.type);

export const PPC_DELIVERY_FEE = 250;

export const PICKUP_LOCATIONS = [
  { id: "ppc_shop", label: "PB-Lux Shop, Puerto Princesa", sub: "In front of Motortrade, San Miguel · Free" },
  { id: "ppc_airport", label: "Puerto Princesa Airport", sub: `We meet you at arrivals · +₱${PPC_DELIVERY_FEE}` },
  { id: "ppc_hotel", label: "Hotel Delivery (Puerto Princesa)", sub: `Delivered to your hotel · +₱${PPC_DELIVERY_FEE}` },
  { id: "el_nido", label: "El Nido Pickup", sub: "Fee by vehicle model/class" },
];

export const DROPOFF_LOCATIONS = [
  { id: "ppc_shop", label: "Return to PB-Lux Shop", sub: "Puerto Princesa · Free" },
  { id: "ppc_airport", label: "Puerto Princesa Airport Return", sub: `We collect at the airport · +₱${PPC_DELIVERY_FEE}` },
  { id: "ppc_hotel", label: "Hotel Collection (Puerto Princesa)", sub: `We collect from your hotel · +₱${PPC_DELIVERY_FEE}` },
  { id: "el_nido", label: "El Nido Drop-off", sub: "Fee by vehicle model/class" },
];

export const locationFeeFor = (locId, vehicle) => {
  if (locId === "el_nido") return elNidoFeeFor(vehicle);
  if (locId === "ppc_airport" || locId === "ppc_hotel") return PPC_DELIVERY_FEE;
  return 0;
};

export const carwashFeeFor = (vehicle) => {
  if (!vehicle || vehicle.type === "motorbike") return 0;
  const cat = (vehicle.category || "").toLowerCase();
  if (cat === "van") return 300;
  if (cat === "pickup" || cat === "suv") return 250;
  return 200;
};

// ===== Reservation fee = one day's effective rental rate =====
export const reservationFeeFor = (vehicle, pickupDate) =>
  effectiveRateFor(vehicle, pickupDate).rate;

// ===== Security: Passport (no cash) OR refundable deposit — Motorbike ₱3,000 · Car ₱5,000 =====
export const securityDepositFor = (vehicle) => {
  if (!vehicle) return 0;
  return vehicle.type === "motorbike" ? 3000 : 5000;
};

// ===== Duration discount: 3+ 7% · 7+ 9% · 15+ 14% · 30+ 18% =====
export const discountPercentForDays = (days) => {
  const d = Number(days) || 0;
  if (d >= 30) return 18;
  if (d >= 15) return 14;
  if (d >= 7) return 9;
  if (d >= 3) return 7;
  return 0;
};

export const DISCOUNT_TIERS = [
  { range: "3–6 days", pct: 7 },
  { range: "7–14 days", pct: 9 },
  { range: "15–29 days", pct: 14 },
  { range: "30+ days", pct: 18 },
];

// Promo rate (if set and lower) overrides the standard rate
export const effectiveRateFor = (vehicle, pickupDate) => {
  const base = Number(vehicle?.daily_rate) || 0;
  const promo = Number(vehicle?.promo_rate) || 0;
  if (promo > 0 && promo < base) return { rate: promo, promo: true, offPeak: false };
  return { rate: base, promo: false, offPeak: false };
};

export const computeVehiclePrice = (vehicle, days, pickupDate) => {
  const { rate, promo, offPeak } = effectiveRateFor(vehicle, pickupDate);
  const d = Math.max(1, Number(days) || 0);
  const fullSubtotal = rate * d;
  const pct = discountPercentForDays(d);
  const discountAmount = Math.round(fullSubtotal * (pct / 100));
  return {
    baseRate: Number(vehicle?.daily_rate) || 0,
    effectiveRate: rate,
    promo,
    offPeak,
    fullSubtotal,
    discountPercent: pct,
    discountAmount,
    subtotal: fullSubtotal - discountAmount,
  };
};

// Legacy signature kept for older components
export const computeRentalPrice = (dailyRate, days, pickupDate) =>
  computeVehiclePrice({ daily_rate: dailyRate }, days, pickupDate);
